/**
 * FibonacciCard - Fibonacci Levels Indicator
 *
 * Displays: Nearest level, distance to level, all Fib levels
 *
 * @version 1.0.0
 */

import React, { memo } from 'react';
import { Box, Typography } from '@mui/material';
import IndicatorCard from '../IndicatorCard';

const FibonacciCard = memo(({ fibLevels, currentPrice }) => {
    const nearestLevel = fibLevels?.nearestLevel;
    const distanceToNearest = fibLevels?.distanceToNearest;
    const levels = fibLevels?.levels || [];
    const swingHigh = fibLevels?.swingHigh;
    const swingLow = fibLevels?.swingLow;

    // Normalize level format
    const normalizeLevel = (level) => {
        if (!level) return '';
        return level.replace(' ', '').replace('.0%', '%');
    };

    // Standard Fibonacci levels
    const standardLevels = ['0%', '23.6%', '38.2%', '50%', '61.8%', '78.6%', '100%'];

    // Map levels
    const levelMap = {};
    levels.forEach(l => {
        const key = normalizeLevel(l.level);
        levelMap[key] = l;
    });

    const summary = (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            {/* Nearest Level */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '10px', color: '#71717A' }}>
                    Nearest:
                </Typography>
                <Typography
                    sx={{
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#fbbf24',
                        fontFamily: 'monospace'
                    }}
                >
                    {normalizeLevel(nearestLevel) || '--'}
                </Typography>
            </Box>

            {/* Distance */}
            {distanceToNearest !== undefined && (
                <Typography
                    sx={{
                        fontSize: '11px',
                        color: distanceToNearest >= 0 ? '#22c55e' : '#ef4444',
                        fontFamily: 'monospace'
                    }}
                >
                    {distanceToNearest >= 0 ? '▲' : '▼'} {Math.abs(distanceToNearest).toFixed(1)} pips
                </Typography>
            )}
        </Box>
    );

    return (
        <IndicatorCard
            title="FIBONACCI"
            icon="📊"
            summary={summary}
            badge={normalizeLevel(nearestLevel)}
            badgeColor="#fbbf24"
            status="neutral"
            defaultExpanded={false}
        >
            {/* Expanded: All Fib levels table */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Swing Range */}
                {swingHigh && swingLow && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '4px 8px',
                            backgroundColor: 'rgba(255, 255, 255, 0.03)',
                            borderRadius: '4px',
                            mb: 0.5
                        }}
                    >
                        <Typography sx={{ fontSize: '9px', color: '#71717A' }}>
                            Swing: {swingLow?.toFixed(2)} → {swingHigh?.toFixed(2)}
                        </Typography>
                    </Box>
                )}

                {/* Levels Grid */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '2px',
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        borderRadius: '4px',
                        padding: '4px'
                    }}
                >
                    {/* Header Row */}
                    {standardLevels.map((level) => {
                        const isNearest = normalizeLevel(nearestLevel) === level;
                        return (
                            <Box
                                key={level}
                                sx={{
                                    textAlign: 'center',
                                    padding: '4px 2px',
                                    backgroundColor: isNearest ? 'rgba(251, 191, 36, 0.15)' : 'transparent',
                                    borderRadius: '2px'
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '9px',
                                        fontWeight: isNearest ? 700 : 500,
                                        color: isNearest ? '#fbbf24' : '#A1A1AA',
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    {level}
                                </Typography>
                            </Box>
                        );
                    })}

                    {/* Price Row */}
                    {standardLevels.map((level) => {
                        const levelData = levelMap[level];
                        const isNearest = normalizeLevel(nearestLevel) === level;
                        const price = levelData?.price;

                        return (
                            <Box
                                key={`price-${level}`}
                                sx={{
                                    textAlign: 'center',
                                    padding: '4px 2px',
                                    backgroundColor: isNearest ? 'rgba(251, 191, 36, 0.1)' : 'transparent',
                                    borderRadius: '2px'
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '9px',
                                        fontWeight: isNearest ? 600 : 400,
                                        color: isNearest ? '#FAFAFA' : '#71717A',
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    {price?.toFixed(0) || '--'}
                                </Typography>
                            </Box>
                        );
                    })}

                    {/* Status Row */}
                    {standardLevels.map((level) => {
                        const levelData = levelMap[level];
                        const isNearest = normalizeLevel(nearestLevel) === level;
                        const status = levelData?.status?.toUpperCase();
                        const arrow = status === 'ABOVE' ? '▲' : status === 'BELOW' ? '▼' : '●';
                        const statusColor = status === 'ABOVE' ? '#22c55e' : status === 'BELOW' ? '#ef4444' : '#fbbf24';

                        return (
                            <Box
                                key={`status-${level}`}
                                sx={{
                                    textAlign: 'center',
                                    padding: '2px',
                                    backgroundColor: isNearest ? 'rgba(251, 191, 36, 0.05)' : 'transparent',
                                    borderRadius: '2px'
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '8px',
                                        color: isNearest ? '#fbbf24' : statusColor
                                    }}
                                >
                                    {arrow}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </IndicatorCard>
    );
});

FibonacciCard.displayName = 'FibonacciCard';

export default FibonacciCard;
